// Boot script: the only file a host page loads (dist/docbox.js). Keep it small. It runs on every
// page view of every customer site, and most visitors never open a document. Anything that is
// not needed before the first interaction belongs in the viewer chunk.
import { API_URLS } from "@docmersion/config";
import { chunkUrl, findScriptSrc } from "./chunk-url";
import { documentLinkFor } from "./links";
import { pdfSubmitUrl } from "./forms";
import { documentUrlFromPreviewSrc, opensByItself } from "./previews";
import {
  includeSameOriginIframes,
  interceptForms,
  interceptIframePdfs,
  interceptSpaNav,
  parseOnAccessibleClickOnly,
  viewMode,
} from "./site-options";
import type { ViewerModule } from "./types";

if (!window.DocmersionWidget) {
  const viewerUrl = chunkUrl(findScriptSrc(document), __VIEWER_CHUNK__);
  const apiBase = __WIDGET_API_BASE__ || API_URLS[__WIDGET_ENV__];
  // Where documents open. Legacy reads this once, when the script loads (docbox.js:4883).
  const mode = viewMode();
  // Read once at load too (docbox.js:4916).
  const parseOnClickOnly = parseOnAccessibleClickOnly();
  let viewer: Promise<ViewerModule> | undefined;
  // The viewer once it has loaded, for checks that must not wait on a load.
  let loaded: ViewerModule | undefined;
  let failedLoads = 0;
  // Folder URLs the viewer gave back to the host page because they did not list. The widget
  // leaves their links alone for the rest of the page view (docboxMarkGoogleReleasedToHost,
  // docbox.js:942).
  const released = new Set<string>();
  const release = (url: string) => void released.add(url);
  const linkFor = (event: Event) => {
    const link = documentLinkFor(event);
    return link && !released.has(link.url) ? link : null;
  };
  // One load at a time. A failed load is forgotten, so the next interaction tries again instead
  // of reusing the failure for the rest of the page view. The browser also remembers a failed
  // module fetch for its exact URL, so a retry asks for the chunk under a fresh query string.
  const loadViewer = () =>
    (viewer ??= (
      import(
        failedLoads ? `${viewerUrl}?retry=${failedLoads}` : viewerUrl
      ) as Promise<ViewerModule>
    ).then(
      (module) => (loaded = module),
      (error: unknown) => {
        viewer = undefined;
        failedLoads += 1;
        throw error;
      },
    ));

  // Document links. Capture phase, so the widget sees the click before the host page's own
  // handlers can open a popup or navigate (legacy host-page fix H1).
  const onClick = (event: MouseEvent) => {
    // A host handler that ran first cancelled the click (docbox.js:35934).
    if (event.defaultPrevented) return;
    const link = linkFor(event);
    if (!link) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey) return;
    if (event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
    loadViewer().then(
      (module) =>
        module.open({
          ...link,
          trigger: link.element,
          apiBase,
          mode,
          parseOnClickOnly,
          release,
        }),
      // The viewer could not load (CDN down, blocked by the host). Do what the link does, in the
      // window of the link: the page, or the iframe the link is in.
      () =>
        (link.element.ownerDocument.defaultView ?? window).location.assign(
          link.url,
        ),
    );
  };

  // Start loading the viewer as soon as a visitor points at or tabs to a document link, so the
  // chunk is usually there by the time they click. Clicks made while it loads still open.
  const warm = (event: Event) => {
    // A failed warm-up is not an error for the host page: the click retries the load.
    if (linkFor(event)) loadViewer().catch(() => {});
  };

  // The documents the widget listens to: the page, and the same-origin iframes below. Each
  // document once (installedDocs, docbox.js:35927).
  const listened = new WeakSet<Document>();
  const listen = (doc: Document) => {
    if (listened.has(doc)) return;
    listened.add(doc);
    doc.addEventListener("click", onClick, true);
    doc.addEventListener("pointerover", warm, true);
    doc.addEventListener("focusin", warm, true);
  };
  listen(document);

  const ready = (run: () => void) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", run, { once: true });
    } else run();
  };

  // Same-origin iframes (DOCBOX_INCLUDE_SAME_ORIGIN_IFRAMES, on by default): document links inside
  // them open the viewer on the page. The widget listens to each iframe's document, again after
  // each load of the iframe (tryInstallInSameOriginIframes, docbox.js:36006-36036). A cross-origin
  // iframe has no contentDocument, so the widget never touches it. Legacy scans only the iframes
  // of the page, not iframes inside iframes. The widget's own iframes live in shadow roots, which
  // the scan does not see (legacy skips them by class, docbox.js:36016-36021).
  const hooked = new WeakSet<HTMLIFrameElement>();
  const hookFrames = () => {
    document.querySelectorAll("iframe").forEach((iframe) => {
      if (hooked.has(iframe)) return;
      hooked.add(iframe);
      const hook = () => {
        try {
          const doc = iframe.contentDocument;
          if (doc) listen(doc);
        } catch {
          // Cross-origin. The browser keeps the widget out.
        }
      };
      iframe.addEventListener("load", hook);
      hook();
    });
  };
  // One scan per animation frame, however many notices arrive (scheduleSameOriginIframeInstall,
  // docbox.js:35995-36004).
  let framesQueued = false;
  const scheduleFrames = () => {
    if (!includeSameOriginIframes() || framesQueued) return;
    framesQueued = true;
    requestAnimationFrame(() => {
      framesQueued = false;
      hookFrames();
    });
  };

  ready(() => {
    if (includeSameOriginIframes()) {
      hookFrames();
      // Iframes added later (docbox.js:36865-36887). Legacy watches <body>.
      new MutationObserver((records) => {
        const added = records.some((record) =>
          [...record.addedNodes].some(
            // nodeType, not instanceof: a node made in an iframe keeps that window's Element.
            (node) =>
              node.nodeType === 1 &&
              ((node as Element).tagName === "IFRAME" ||
                !!(node as Element).querySelector("iframe")),
          ),
        );
        if (added) scheduleFrames();
      }).observe(document.body, { childList: true, subtree: true });
    }

    // A history navigation scans for iframes again (DOCBOX_INTERCEPT_SPA_NAV, on by default):
    // an SPA can mount new iframes where the observer does not see them, such as a new <body>
    // (installSpaNavigationHooks, docbox.js:36038-36060).
    if (interceptSpaNav()) {
      const navigated = () => setTimeout(scheduleFrames, 0);
      // A host that locked the history methods keeps them. The rest still installs, as in legacy.
      for (const name of ["pushState", "replaceState"] as const) {
        try {
          const original = history[name];
          history[name] = function (this: History, ...args) {
            const result = original.apply(this, args);
            navigated();
            return result;
          } as History[typeof name];
        } catch {
          // Read-only. Strict mode throws on the assignment.
        }
      }
      window.addEventListener("popstate", navigated);
      window.addEventListener("hashchange", navigated);
    }

    // A GET form that submits to a PDF opens the viewer (DOCBOX_INTERCEPT_FORMS, on by default).
    // Legacy listens on the page only, not in iframes (docbox.js:36842).
    if (interceptForms()) {
      document.addEventListener(
        "submit",
        (event) => {
          if (event.defaultPrevented) return;
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          // A form of the widget's own UI reaches the page retargeted to its host element.
          if (form.closest("[data-docmersion-widget]")) return;
          const submitter = event.submitter ?? document.activeElement;
          const url = pdfSubmitUrl(form, submitter);
          // The Lambda refuses any URL that is not HTTPS, so such a submit stays with the host.
          if (!url?.startsWith("https://")) return;
          event.preventDefault();
          const trigger = submitter instanceof HTMLElement ? submitter : form;
          loadViewer().then(
            (module) =>
              module.open({
                url,
                title: (submitter?.textContent ?? "")
                  .replace(/\s+/g, " ")
                  .trim(),
                trigger,
                apiBase,
                mode,
                parseOnClickOnly,
                release,
              }),
            // The viewer could not load. Open the URL the submit asked for.
            () => window.location.assign(url),
          );
        },
        true,
      );
    }
  });

  // Embedded previews, watched by default as in legacy (installIframePdfWatcher,
  // docbox.js:36409). DOCBOX_INTERCEPT_IFRAME_PDFS = false turns them off (docbox.js:36410). The
  // script tag's data-previews attribute is no longer read.
  if (interceptIframePdfs()) {
    // Elements to check on the next frame: added nodes, iframes whose src changed, and skipped
    // previews that changed size. One pass per animation frame, however many records arrive.
    const pending = new Set<Element>();
    let frame = 0;
    const schedule = () => {
      frame ||= requestAnimationFrame(flush);
    };
    // The URL each wrapped iframe was last wrapped for, so a repeat notice for the same document
    // does nothing.
    const wrapped = new Map<HTMLIFrameElement, string>();
    // Previews hidden when checked. Checked again when their size changes.
    const skipped = new Set<HTMLIFrameElement>();
    const sizeWatch = new ResizeObserver((entries) => {
      entries.forEach((entry) => pending.add(entry.target));
      schedule();
    });
    const skip = (iframe: HTMLIFrameElement) => {
      if (skipped.has(iframe)) return;
      skipped.add(iframe);
      sizeWatch.observe(iframe);
    };
    const unskip = (iframe: HTMLIFrameElement) => {
      if (skipped.delete(iframe)) sizeWatch.unobserve(iframe);
    };
    // Previews that open on a click, each with the button over it and the document it opens
    // (bindTinyHostPreviewClickOpen, docbox.js:36218).
    const cards = new Map<HTMLIFrameElement, Card>();
    const cardBoxes = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const card = cards.get(entry.target as HTMLIFrameElement);
        if (card) placeCard(card);
      });
    });
    const addCard = (iframe: HTMLIFrameElement, url: string) => {
      const existing = cards.get(iframe);
      // A card stays while its button sits next to the preview. The host may remove the button,
      // or move the preview to another container.
      if (
        existing?.host.isConnected &&
        existing.host.parentElement === iframe.parentElement
      ) {
        existing.url = url;
        placeCard(existing);
        return;
      }
      if (existing) removeCard(iframe);
      // The document the card opens now: the host may change the src after the card is made.
      const current = () => cards.get(iframe)?.url ?? url;
      const card = createCard(iframe, url, (button) =>
        loadViewer().then(
          (module) =>
            module.open({
              url: current(),
              title: iframe.title.trim(),
              trigger: button,
              apiBase,
              mode,
              parseOnClickOnly,
            }),
          // The viewer could not load. The preview stays as the host made it.
          () => {},
        ),
      );
      if (!card) return;
      // Start loading the viewer on the press, before the click (docbox.js:36275).
      card.host.addEventListener("pointerdown", () =>
        loadViewer().catch(() => {}),
      );
      cards.set(iframe, card);
      cardBoxes.observe(iframe);
    };
    const removeCard = (iframe: HTMLIFrameElement) => {
      const card = cards.get(iframe);
      if (!card) return;
      cards.delete(iframe);
      cardBoxes.unobserve(iframe);
      card.host.remove();
    };
    // Removes the toolbar and gives the host its preview back as it was.
    const unwrap = (iframe: HTMLIFrameElement) => {
      wrapped.delete(iframe);
      loadViewer().then(
        (module) => module.unwrapPreview(iframe),
        () => {},
      );
    };

    const considerPreview = (iframe: HTMLIFrameElement) => {
      if (!iframe.isConnected) return;
      const url = documentUrlFromPreviewSrc(iframe.src);
      if (!url) {
        // Not a preview, or no longer one: the host set an unsupported or empty src.
        unskip(iframe);
        removeCard(iframe);
        if (wrapped.has(iframe)) unwrap(iframe);
        return;
      }
      // The same document again does nothing, unless the host removed the toolbar and kept the
      // preview. Then the preview may still be hidden behind Transcript with no toolbar to bring
      // it back, so wrap it again: that restores the preview and adds a new toolbar.
      if (wrapped.get(iframe) === url && !loaded?.toolbarRemoved(iframe)) {
        return;
      }
      // The open decision applies the first time only. A wrapped preview hidden behind
      // Transcript measures 0×0, and its toolbar must still follow a new document.
      if (!wrapped.has(iframe)) {
        // Not painted yet: decide when it has a size (scheduleIframeSizeRecheck,
        // docbox.js:36506-36509).
        const box = iframe.getBoundingClientRect();
        if (!(box.width > 0 && box.height > 0)) {
          skip(iframe);
          return;
        }
        unskip(iframe);
        // A small card, a phone or tablet, or a site option: the host keeps its preview, and a
        // click on it opens the viewer (docbox.js:36510-36516).
        if (!opensByItself(box)) {
          addCard(iframe, url);
          return;
        }
      }
      // A large preview on a desktop opens the accessible view in its place, with focus left
      // where it is (docbox.js:32690). Original shows first, as on a link click.
      unskip(iframe);
      removeCard(iframe);
      wrapped.set(iframe, url);
      const title = iframe.title.trim();
      loadViewer().then(
        (module) => module.wrapPreview({ iframe, url, title, apiBase }),
        // The viewer could not load. The preview stays as the host made it.
        () => wrapped.delete(iframe),
      );
    };

    const flush = () => {
      frame = 0;
      // The host removed a preview (a React re-render): drop its toolbar, and stop watching it.
      // The host removed only the toolbar: check the preview again.
      wrapped.forEach((_url, iframe) => {
        if (!iframe.isConnected) unwrap(iframe);
        else if (loaded?.toolbarRemoved(iframe)) pending.add(iframe);
      });
      skipped.forEach((iframe) => {
        if (!iframe.isConnected) unskip(iframe);
      });
      // The host removed a card's preview: drop the card. The host removed only the button, or
      // moved the preview: check the preview again. Any other change to the page can move a
      // preview without a resize, so each button goes back onto its preview.
      cards.forEach((card, iframe) => {
        if (!iframe.isConnected) removeCard(iframe);
        else if (card.host.parentElement !== iframe.parentElement) {
          removeCard(iframe);
          pending.add(iframe);
        } else placeCard(card);
      });
      const candidates = new Set<HTMLIFrameElement>();
      pending.forEach((element) => {
        if (element instanceof HTMLIFrameElement) candidates.add(element);
        else
          element
            .querySelectorAll("iframe")
            .forEach((iframe) => candidates.add(iframe));
      });
      pending.clear();
      candidates.forEach(considerPreview);
    };

    // SchoolBlocks inserts preview iframes after load and sets their src after mount
    // (ThirdPartyIframe), and a terrace swaps src when a visitor picks another file. Watch for
    // both, and for removals. The widget's own iframes live in shadow roots, which this observer
    // does not see.
    new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === "attributes") {
          if (record.target instanceof HTMLIFrameElement) {
            pending.add(record.target);
          }
          continue;
        }
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) pending.add(node);
        });
      }
      schedule();
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });
    document.querySelectorAll("iframe").forEach(considerPreview);
  }

  window.DocmersionWidget = {
    version: __WIDGET_VERSION__,
    apiBase,
    loadViewer,
  };
  // The Disability Assistance helper page (GET /assist/{code} on the API) names the document the
  // volunteer opens. Open it at once, as legacy did (docbox.js:36804).
  const assistUrl = window.DOCBOX_ASSIST_OPEN_URL;
  if (typeof assistUrl === "string" && assistUrl.trim()) {
    loadViewer().then(
      (module) =>
        module.open({
          url: assistUrl.trim(),
          title: "",
          trigger: null,
          apiBase,
        }),
      () => {},
    );
  }
}

type Card = { iframe: HTMLIFrameElement; host: HTMLElement; url: string };

/**
 * A transparent button over a preview, in the preview's parent (bindTinyHostPreviewClickOpen,
 * docbox.js:36218-36294). A static parent becomes relative so the button can sit on the preview,
 * and stays relative, as in legacy. The button lives in a shadow root, so host styles do not reach
 * it. It comes before the preview, so the keyboard reaches it before the preview's own controls.
 */
function createCard(
  iframe: HTMLIFrameElement,
  url: string,
  onOpen: (button: HTMLButtonElement) => void,
): Card | null {
  const parent = iframe.parentElement;
  if (!parent) return null;
  if (getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }
  const host = document.createElement("div");
  host.setAttribute("data-docmersion-widget", "");
  host.style.cssText = "position:absolute;z-index:5;margin:0;padding:0;";
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("aria-label", cardLabel(iframe));
  button.style.cssText =
    "display:block;width:100%;height:100%;margin:0;padding:0;border:0;background:transparent;cursor:pointer;appearance:none;-webkit-appearance:none;";
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    onOpen(button);
  });
  host.attachShadow({ mode: "open" }).append(button);
  iframe.before(host);
  const card = { iframe, host, url };
  placeCard(card);
  return card;
}

/**
 * Puts the button on the preview's box (syncTinyHostPreviewHitBox, docbox.js:36296). A hidden
 * preview hides its button too, so the keyboard cannot reach a button with nothing under it.
 */
function placeCard({ iframe, host }: Card): void {
  const hidden = !iframe.offsetWidth || !iframe.offsetHeight;
  Object.assign(host.style, {
    display: hidden ? "none" : "",
    left: `${iframe.offsetLeft}px`,
    top: `${iframe.offsetTop}px`,
    width: `${iframe.offsetWidth}px`,
    height: `${iframe.offsetHeight}px`,
  });
}

/** "Open " and the heading of the preview's article, or "Open document" (docbox.js:36248-36254). */
function cardLabel(iframe: HTMLIFrameElement): string {
  const heading = iframe.closest("article")?.querySelector("h1,h2,h3,h4,h5");
  const text = (heading?.textContent ?? "").replace(/\s+/g, " ").trim();
  return text ? `Open ${text}` : "Open document";
}
